import { Action } from "common/picker/action/types";
import {
  AutocompleteState,
  State,
  TagListState,
} from "common/picker/reducer/types";
import { State as InputState } from "common/types/global";
import { concat, isEmpty, without } from "lodash";

// tslint:disable:interface-name
export interface ReducerIO {
  capacity: number;
  userEmail?: string;
  placeholder: string;
  selectedSearchBy?: string;
  displaySearchBy?: boolean;
  state: InputState;
  disabled?: boolean;
}

export interface UpdatedPropsIO {
  capacity?: number;
  userEmail?: string;
  placeholder?: string;
  selectedSearchBy?: string;
  displaySearchBy?: boolean;
}

export const getReducer = <TItem, TCriteria>(init: State<TItem, TCriteria>) => (
  state = init,
  action: Action<TItem, TCriteria>
): State<TItem, TCriteria> => {
  switch (action.type) {
    case "ON_START":
      return onStart(state, action.items, action.updatedProps);
    case "ON_BLUR":
      return onBlur(state);
    case "ON_ERROR":
      return onError(state, action.reason);
    case "ON_FOCUS":
      return onFocus(state);
    case "ON_INPUT":
      return onInput(state, action.terms, action.criteria);
    case "ON_REMOVE":
      return onRemove(state, action.item);
    case "ON_SEARCH":
      return onSearch(state, action.items);
    case "ON_SELECT":
      return onSelect(state, action.item);
    default:
      return state;
  }
};

export function initState<TItem, TCriteria>(
  dependencies: ReducerIO
): State<TItem, TCriteria> {
  const tagList: TagListState<TItem> = {
    disabled: dependencies.capacity <= 0 || dependencies.disabled || false,
    displaySearchBy: dependencies.displaySearchBy || true,
    hasPortfolio: dependencies.userEmail !== "",
    placeholder: dependencies.placeholder,
    selectedSearchBy: dependencies.selectedSearchBy || "",
    state: dependencies.state || "empty",
    status: "init",
    tags: [],
  };
  const autocomplete: AutocompleteState<TItem> = {
    exclude: [],
    isVisible: false,
    items: [],
    reason: "",
    status: "init",
  };
  return {
    autocomplete,
    capacity: dependencies.capacity,
    criteria: undefined,
    tagList,
    terms: "",
  };
}

export function reinitState<TItem, TCriteria>(
  dependencies: ReducerIO,
  state: State<TItem, TCriteria>
): State<TItem, TCriteria> {
  const tagList: TagListState<TItem> = {
    ...state.tagList,
    disabled: dependencies.capacity <= 0 || dependencies.disabled || false,
    displaySearchBy: dependencies.displaySearchBy || true,
    hasPortfolio: dependencies.userEmail !== "",
    placeholder: dependencies.placeholder,
    selectedSearchBy: dependencies.selectedSearchBy || "",
  };
  const autocomplete: AutocompleteState<TItem> = {
    ...state.autocomplete,
  };
  return {
    ...state,
    autocomplete,
    capacity: dependencies.capacity,
    tagList,
  };
}

export function onStart<TItem, TCriteria>(
  state: State<TItem, TCriteria>,
  items: TItem[],
  updatedProps: UpdatedPropsIO
): State<TItem, TCriteria> {
  const tags: TItem[] = items;

  const autocomplete: AutocompleteState<TItem> = {
    ...state.autocomplete,
    exclude: tags,
  };

  const tagList: TagListState<TItem> = {
    ...state.tagList,
    disabled: tags.length >= state.capacity,
    ...(updatedProps.selectedSearchBy
      ? { selectedSearchBy: updatedProps.selectedSearchBy }
      : {}),
    tags,
  };
  return { ...state, autocomplete, tagList };
}

export function onFocus<TItem, TCriteria>(
  state: State<TItem, TCriteria>
): State<TItem, TCriteria> {
  const autocomplete: AutocompleteState<TItem> = {
    ...state.autocomplete,
    isVisible: true,
  };
  const tagList: TagListState<TItem> = { ...state.tagList };
  return { ...state, autocomplete, tagList };
}

export function onBlur<TItem, TCriteria>(
  state: State<TItem, TCriteria>
): State<TItem, TCriteria> {
  const autocomplete: AutocompleteState<TItem> = {
    ...state.autocomplete,
    isVisible: false,
  };
  const tagList: TagListState<TItem> = { ...state.tagList };
  return { ...state, autocomplete, tagList };
}

export function onRemove<TItem, TCriteria>(
  state: State<TItem, TCriteria>,
  item: TItem
): State<TItem, TCriteria> {
  const tags = without(state.tagList.tags, item);
  const autocomplete: AutocompleteState<TItem> = {
    ...state.autocomplete,
    exclude: tags,
  };
  const tagList: TagListState<TItem> = {
    ...state.tagList,
    tags,
    disabled: tags.length >= state.capacity,
  };
  return { ...state, autocomplete, tagList };
}

export function onSelect<TItem, TCriteria>(
  state: State<TItem, TCriteria>,
  item: TItem
): State<TItem, TCriteria> {
  const tagList: TagListState<TItem> = {
    ...state.tagList,
    disabled: state.tagList.tags.length >= state.capacity - 1,
    tags: concat(state.tagList.tags, item),
  };
  const autocomplete: AutocompleteState<TItem> = {
    ...state.autocomplete,
    exclude: tagList.tags,
    isVisible: false,
    status: "init",
  };

  return { ...state, criteria: undefined, terms: "", tagList, autocomplete };
}

export function onSearch<TItem, TCriteria>(
  state: State<TItem, TCriteria>,
  items: TItem[]
): State<TItem, TCriteria> {
  const status = isEmpty(items) ? "empty" : "loaded";
  const autocomplete: AutocompleteState<TItem> = {
    ...state.autocomplete,
    exclude: state.tagList.tags,
    items,
    status,
  };

  return { ...state, autocomplete };
}

export function onError<TItem, TCriteria>(
  state: State<TItem, TCriteria>,
  reason: string
): State<TItem, TCriteria> {
  return {
    ...state,
    autocomplete: { ...state.autocomplete, status: "error", reason },
  };
}

export function onInput<TItem, TCriteria>(
  state: State<TItem, TCriteria>,
  terms: string,
  criteria: TCriteria
): State<TItem, TCriteria> {
  return {
    ...state,
    criteria,
    terms,
    autocomplete: { ...state.autocomplete, status: "loading" },
  };
}
